/**
 * What Home last showed, kept for the life of the tab, so coming back from a file draws the
 * page at once instead of the grey placeholders (Tarek, 2026-10-31: "every time I go from an
 * artwork to home I see the loading page"). Home is a route of its own, so opening a file
 * unmounts it and its state goes with it; these keep the last answers in memory. Every one is
 * asked again in the background on the way back, and the fresh answer replaces it quietly.
 *
 * Keyed by workspace (and list), so one workspace's files never show in another. Nothing here
 * outlives a reload.
 */
import type { BrandCard } from '../services/work';
import type { FileRow, ProjectInfo, ProjectSummary } from './rows';

/** How many lists are kept: the recents, the probe, a few projects and searches. */
const MAX_LISTS = 24;

export interface CachedList<R extends FileRow = FileRow> {
  rows: R[];
  more: boolean;
}

const lists = new Map<string, CachedList>();

export function cachedList<R extends FileRow>(key: string): CachedList<R> | undefined {
  return lists.get(key) as CachedList<R> | undefined;
}

export function rememberList<R extends FileRow>(key: string, value: CachedList<R> | null): void {
  lists.delete(key);
  if (!value) return;
  lists.set(key, value as CachedList);
  // The oldest goes first: a Map keeps the order things were set in.
  while (lists.size > MAX_LISTS) lists.delete(lists.keys().next().value!);
}

/** One answer per workspace: the projects' names, their Artwork summaries, the brands. */
function perWorkspace<T>() {
  const byOrg = new Map<string, T>();
  return {
    get: (org: string) => byOrg.get(org),
    set: (org: string, value: T) => void byOrg.set(org, value),
  };
}

export const cachedInfos = perWorkspace<Map<string, ProjectInfo>>();
export const cachedSummaries = perWorkspace<ProjectSummary[] | null>();
export const cachedBrands = perWorkspace<BrandCard[]>();

/** Tests only: forget everything. */
export function forgetHome(): void {
  lists.clear();
}
